import React, { useState } from 'react';
import { doc, writeBatch } from 'firebase/firestore';
import { db } from './firebase';

export default function StaffBatchImporter({ tenantId }) {
  const [csvData, setCsvData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [importMessage, setImportMessage] = useState('');

  // --- THE TRANSLATION ENGINE ---
  // Add keywords here based on what Infinite Campus actually exports
  const roleDictionary = {
    'spec': 'Case Manager',
    'sped': 'Case Manager',
    'ece': 'Case Manager',
    'resource': 'Case Manager',
    'para': 'Instructional Assistant',
    'assistant': 'Instructional Assistant',
    'admin': 'District Admin',
    'principal': 'District Admin',
    'director': 'District Admin'
  };

  const sanitizeRole = (rawRole) => {
    if (!rawRole) return 'Gen Ed'; // Safety default
    
    const cleanRaw = rawRole.toLowerCase();
    
    // Scan the dirty IC string for our dictionary keywords
    for (const [keyword, mappedRole] of Object.entries(roleDictionary)) {
      if (cleanRaw.includes(keyword)) {
        return mappedRole;
      }
    }
    
    // If we don't recognize the job title, they get the basic Walled Garden
    return 'Gen Ed'; 
  };

  // --- NATIVE CSV PARSER ---
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      
      const rows = text.split(/\r?\n/).filter(row => row.trim() !== '');
      
      if (rows.length < 2) {
        setImportMessage("CSV appears to be empty or missing data.");
        return;
      }

      const parsedHeaders = rows[0].split(',').map(h => h.trim().toLowerCase());
      setHeaders(parsedHeaders);

      const parsedData = [];
      for (let i = 1; i < rows.length; i++) {
        const values = rows[i].split(',').map(v => v.trim());
        const rowObject = {};
        
        parsedHeaders.forEach((header, index) => {
          rowObject[header] = values[index] || '';
        });
        
        // Only process if an email exists
        if (rowObject.email) {
          // Send the dirty Infinite Campus role through our translator!
          const translatedRole = sanitizeRole(rowObject.role);
          
          parsedData.push({
            ...rowObject,
            rawRole: rowObject.role || 'Unknown', // Keep this just so you can see what IC originally said
            mappedRole: translatedRole
          });
        }
      }
      
      setCsvData(parsedData);
      setImportMessage(`Successfully parsed and translated ${parsedData.length} staff records.`);
    };
    
    reader.readAsText(file);
  };

  // --- FIREBASE BATCH EXECUTION ---
  const handleBatchImport = async () => {
    if (!tenantId || csvData.length === 0) return;
    setIsProcessing(true);
    setImportMessage('Executing secure batch import...');

    try {
      const batch = writeBatch(db);

      csvData.forEach((staff) => {
        const cleanEmail = staff.email.toLowerCase();
        
        const profileData = {
          name: `${staff.firstname || ''} ${staff.lastname || ''}`.trim(),
          email: cleanEmail,
          role: staff.mappedRole, // We push the clean, translated role to the database
          schoolId: staff.school || 'District Office',
          status: 'Pending Registration', 
        };

        const userRef = doc(db, `districts/${tenantId}/users`, cleanEmail);
        batch.set(userRef, profileData);
      });

      await batch.commit();

      setImportMessage(`🎉 Success! ${csvData.length} staff members securely whitelisted.`);
      setCsvData([]); 
    } catch (error) {
      console.error("Batch Import Failed:", error);
      setImportMessage("Critical error during batch import. Check console.");
    } finally {
      setIsProcessing(false);
    }
  };

  const clearData = () => {
    setCsvData([]);
    setHeaders([]);
    setImportMessage('');
  };

  return (
    <div style={{ backgroundColor: '#fff', padding: '30px', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', fontFamily: 'sans-serif' }}>
      
      <div style={{ borderBottom: '2px solid #003366', paddingBottom: '15px', marginBottom: '25px' }}>
        <h2 style={{ margin: 0, color: '#003366', fontSize: '24px' }}>God Mode: Mass Importer</h2>
        <p style={{ margin: '5px 0 0 0', color: '#666', fontSize: '13px' }}>
          Upload a CSV with headers: <strong>email, firstname, lastname, role, school</strong>
        </p>
      </div>

      {csvData.length === 0 ? (
        <div style={{ padding: '40px', border: '2px dashed #ccc', borderRadius: '8px', textAlign: 'center', backgroundColor: '#fafafa' }}>
          <input 
            type="file" 
            accept=".csv" 
            onChange={handleFileUpload}
            style={{ display: 'block', margin: '0 auto 15px auto' }}
          />
          <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>Select a CSV file to preview the data.</p>
        </div>
      ) : (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <span style={{ fontWeight: 'bold', color: '#003366' }}>{importMessage}</span>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button 
                onClick={clearData} disabled={isProcessing}
                style={{ padding: '8px 16px', backgroundColor: '#fff', color: '#ba0c2f', border: '1px solid #ba0c2f', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
              >
                Cancel
              </button>
              <button 
                onClick={handleBatchImport} disabled={isProcessing}
                style={{ padding: '8px 16px', backgroundColor: '#28a745', color: '#fff', border: 'none', borderRadius: '4px', cursor: isProcessing ? 'not-allowed' : 'pointer', fontWeight: 'bold' }}
              >
                {isProcessing ? 'Committing...' : `Commit ${csvData.length} Users`}
              </button>
            </div>
          </div>

          {/* PREVIEW TABLE */}
          <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #eee', borderRadius: '4px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead style={{ backgroundColor: '#003366', color: '#fff', position: 'sticky', top: 0 }}>
                <tr>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Email</th>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Name</th>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Original IC Role</th>
                  <th style={{ padding: '10px', textAlign: 'left', backgroundColor: '#28a745' }}>CaseSync Role</th>
                  <th style={{ padding: '10px', textAlign: 'left' }}>School</th>
                </tr>
              </thead>
              <tbody>
                {csvData.map((row, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #eee', backgroundColor: idx % 2 === 0 ? '#fff' : '#f9f9f9' }}>
                    <td style={{ padding: '8px 10px', color: '#003366', fontWeight: 'bold' }}>{row.email}</td>
                    <td style={{ padding: '8px 10px' }}>{row.firstname} {row.lastname}</td>
                    <td style={{ padding: '8px 10px', color: '#999', fontStyle: 'italic' }}>{row.rawRole}</td>
                    <td style={{ padding: '8px 10px', fontWeight: 'bold', color: row.mappedRole === 'Gen Ed' ? '#666' : '#28a745' }}>
                      {row.mappedRole}
                    </td>
                    <td style={{ padding: '8px 10px' }}>{row.school || 'District Office'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

    </div>
  );
}